import React, { useEffect, useRef, useState } from 'react';

const TimeInput = ({ value, onChange, min, max, label, disabled }) => {
    const [showOptions, setShowOptions] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowOptions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const generateOptions = () => {
        const options = [];
        for (let i = min; i <= max; i++) {
            options.push(i.toString().padStart(2, '0'));
        }
        return options;
    };

    const wrapperStyle = (label) => ({
        position: 'relative',
        backgroundColor: '#f3f4f6',
        border: '1px solid #d1d5db',
        borderRadius: label === 'H' ? '0.75rem 0 0 0.75rem' : '0 0.75rem 0.75rem 0',
        width: '58px',
        height: '36px',
        fontSize: '14px',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
    });

    const inputStyle = {
        width: '100%',
        height: '100%',
        textAlign: 'center',
        backgroundColor: 'transparent',
        outline: 'none',
        border: 'none',
        fontSize: 'inherit',
        fontWeight: 'inherit',
        color: '#000',
        pointerEvents: 'none',
    };

    const labelStyle = {
        position: 'absolute',
        right: '8px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#9ca3af',
        fontSize: 'inherit',
        fontWeight: 'inherit',
    };

    const dropdownStyle = {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        background: '#fff',
        border: '1px solid #d1d5db',
        zIndex: 1000,
        maxHeight: '150px',
        overflowY: 'auto',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        borderRadius: '0.5rem',
    };

    const optionStyle = {
        padding: '8px',
        textAlign: 'center',
        cursor: 'pointer',
        fontWeight: 700,
        fontSize: '14px',
        lineHeight: '20px'
    };

    const handleSelect = (e, val) => {
        e.stopPropagation();
        const syntheticEvent = { target: { value: val } };
        setShowOptions(false);
        onChange(syntheticEvent);
    };

    return (
        <div
            ref={wrapperRef}
            style={wrapperStyle(label)}
            onClick={() => !disabled && setShowOptions(true)}
        >
            <input
                type="text"
                readOnly
                value={value === '-' ? '-' : value}
                style={inputStyle}
                disabled={disabled}
            />
            <span style={labelStyle}>{label}</span>

            {showOptions && (
                <div style={dropdownStyle}>
                    {generateOptions().map((opt) => (
                        <div
                            key={opt}
                            style={{
                                ...optionStyle,
                                backgroundColor: 'white',
                                color: opt === value ? '#00B0F5' : '#000'
                            }}
                            onClick={(e) => handleSelect(e, opt)}
                        >
                            {opt}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const AmPmToggle = ({ value, onChange, disabled }) => {
    const wrapper = {
        display: 'flex',
        border: '1px solid #d1d5db',
        borderRadius: '0.75rem',
        overflow: 'hidden',
        height: '36px',
    };

    const optionStyle = (selected) => ({
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '50px',
        fontWeight: '600',
        fontSize: '14px',
        color: selected === value ? '#000' : '#9ca3af',
        backgroundColor: selected === value ? '#fff' : '#f9f9f9',
        border: selected === value ? '2px solid #000' : 'none',
        borderRadius: selected === 'AM' ? '0.75rem 0 0 0.75rem' : '0 0.75rem 0.75rem 0',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
    });

    return (
        <div style={wrapper}>
            <div style={optionStyle('AM')} onClick={!disabled ? () => onChange('AM') : undefined}>
                AM
            </div>
            <div style={optionStyle('PM')} onClick={!disabled ? () => onChange('PM') : undefined}>
                PM
            </div>
        </div>
    );
};

const ATimePicker = ({ timestamp, handler, disabled = false, minTime, maxTime }) => {
    const [hours, setHours] = useState('-');
    const [minutes, setMinutes] = useState('-');
    const [period, setPeriod] = useState('AM');

    const to24HourTime = (h, m, period) => {
        h = parseInt(h, 10);
        m = parseInt(m, 10);
        if (period === 'PM' && h !== 12) h += 12;
        if (period === 'AM' && h === 12) h = 0;
        return { hours: h, minutes: m };
    };

    const isTimeInRange = (h, m) => {
        const current = h * 60 + m;
        const min = minTime ? parseInt(minTime.split(':')[0]) * 60 + parseInt(minTime.split(':')[1]) : null;
        const max = maxTime ? parseInt(maxTime.split(':')[0]) * 60 + parseInt(maxTime.split(':')[1]) : null;
        return (min === null || current >= min) && (max === null || current <= max);
    };

    const initializeFromTimestamp = () => {
        if (typeof timestamp === 'string' && /^\d{1,2}:\d{2}$/.test(timestamp)) {
            const [h, m] = timestamp.split(':').map(Number);
            if (!isNaN(h) && !isNaN(m)) {
                const isPM = h >= 12;
                const displayHour = h % 12 || 12;
                setPeriod(isPM ? 'PM' : 'AM');
                setHours(displayHour.toString().padStart(2, '0'));
                setMinutes(m.toString().padStart(2, '0'));
                return;
            }
        }
        setHours('-');
        setMinutes('-');
        setPeriod('AM');
    };

    useEffect(() => {
        initializeFromTimestamp();
    }, [timestamp]);

    useEffect(() => {
        if (disabled || hours === '-' || minutes === '-') return;

        const { hours: h24, minutes: m } = to24HourTime(hours, minutes, period);
        if (!isTimeInRange(h24, m)) return;

        const formatted = `${h24.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
        handler(formatted);
    }, [hours, minutes, period]);

    const handleHoursChange = (e) => {
        let val = e.target.value.padStart(2, '0');
        const num = Number(val);
        if (isNaN(num)) {
            setHours('-');
            return;
        }
        val = Math.max(1, Math.min(12, num)).toString().padStart(2, '0');
        const { hours: h24, minutes: m } = to24HourTime(val, minutes === '-' ? '00' : minutes, period);
        if (isTimeInRange(h24, m)) setHours(val);
    };

    const handleMinutesChange = (e) => {
        let val = e.target.value.padStart(2, '0');
        const num = Number(val);
        if (isNaN(num)) {
            setMinutes('-');
            return;
        }
        val = Math.max(0, Math.min(59, num)).toString().padStart(2, '0');
        const { hours: h24, minutes: m } = to24HourTime(hours === '-' ? '12' : hours, val, period);
        if (isTimeInRange(h24, m)) setMinutes(val);
    };

    const handlePeriodChange = (newPeriod) => {
        const { hours: h24, minutes: m } = to24HourTime(hours === '-' ? '12' : hours, minutes === '-' ? '00' : minutes, newPeriod);
        if (isTimeInRange(h24, m)) setPeriod(newPeriod);
    };

    return (
        <>
            <div style={{ display: 'flex', gap: '4px' }}>
                <div style={{ display: 'flex', gap: '2px' }}>
                    <TimeInput
                        value={hours}
                        onChange={handleHoursChange}
                        min={1}
                        max={12}
                        label="H"
                        disabled={disabled}
                    />
                    <TimeInput
                        value={minutes}
                        onChange={handleMinutesChange}
                        min={0}
                        max={59}
                        label="M"
                        disabled={disabled}
                    />
                </div>
                <AmPmToggle value={period} onChange={handlePeriodChange} disabled={disabled} />
            </div>
        </>
    );
};

export default ATimePicker;
